import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, SealCheck } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";
import { CATEGORY_META } from "@/data/categories";
import { priceLabel } from "@/lib/format";
import { FoundingBadge } from "@/components/FoundingBadge";
import { RatingBadge } from "@/components/RatingStars";
import { ProviderAvatar } from "@/components/AvatarUpload";

export const ServiceCard = ({ listing, size = "md" }) => {
  const { t, tc } = useLang();
  const meta = CATEGORY_META[listing.category] || {};
  const Icon = meta.icon;
  const price = priceLabel(listing, t);

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`listing-card-${listing.id}`}
      className={`svc-card group hover-lift ${size === "lg" ? "svc-card-lg" : ""}`}
    >
      <img src={listing.image || meta.image} alt={tc(listing.category)} loading="lazy" className="svc-card-img" />
      <span className="svc-card-veil" aria-hidden="true" />
      <div className="relative flex h-full flex-col justify-between p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="glass-pill flex min-w-0 items-center gap-2">
            <ProviderAvatar
              name={listing.provider_name}
              src={listing.provider_avatar ? `${process.env.REACT_APP_BACKEND_URL}${listing.provider_avatar}` : null}
              size={26}
            />
            <span className="truncate text-[12.5px] font-medium text-ppc-text">{listing.provider_name}</span>
            {listing.provider_verified && <SealCheck size={13} weight="fill" color="#9184d9" className="shrink-0" />}
          </span>
          <span className="glass-pill shrink-0 text-[13px] font-medium text-ppc-text">
            {price.main}
            <span className="ml-0.5 text-[10.5px] text-ppc-muted">{price.unit}</span>
          </span>
        </div>

        <div>
          {listing.founding_member && <FoundingBadge fm={listing.founding_member} className="mb-2" />}
          <span className="glass-pill inline-flex items-center gap-1.5 text-[12px] text-ppc-text">
            {Icon && <Icon className="h-3.5 w-3.5" style={{ color: "#c6bff0" }} />} {tc(listing.category)}
          </span>
          <h3 className="mt-2 flex items-center gap-1.5 text-[17px] font-medium leading-snug text-ppc-text">
            <span className="line-clamp-1">{listing.title}</span>
            {(listing.is_business || listing.provider_verified) && (
              <SealCheck size={15} weight="fill" color="#9184d9" className="shrink-0" />
            )}
          </h3>
          <p className="mt-1 flex items-center gap-2 text-[12.5px] text-ppc-muted">
            <span className="truncate text-ppc-text">{listing.provider_name}</span>
            {listing.rating_count > 0 && <RatingBadge avg={listing.rating} count={listing.rating_count} />}
          </p>
          <p className="svc-card-desc mt-1 line-clamp-2">{listing.description}</p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="glass-pill flex min-w-0 items-center gap-1.5 text-[12px] text-ppc-text">
              <MapPin size={13} weight="fill" color="#c6bff0" />
              <span className="truncate">{listing.city}, {listing.country}</span>
            </span>
            <span className="glass-pill flex shrink-0 items-center gap-1.5 text-[12px] text-ppc-text transition-colors group-hover:text-primary-green">
              {t("hero_cta")}
              <ArrowUpRight size={13} weight="bold" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
